import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../contexts/AuthContext';
import { shJson } from './api';

export default function SafetyHuddleHubPage() {
  const navigate = useNavigate();
  const { unidadeSelecionada, isAdmin, rbacRole } = useAuth();
  // GLOBAL_VIEWER = somente leitura (não cria/edita setores)
  const canManage =
    isAdmin ||
    ['SUPER_ADMIN', 'LOCAL_ADMIN', 'LOCAL_APPROVER'].includes(rbacRole);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sectors, setSectors] = useState([]);
  const [seeAll, setSeeAll] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [integraContratos, setIntegraContratos] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!unidadeSelecionada?.codigo) {
      setSectors([]);
      setLoading(false);
      setError('Selecione uma unidade para carregar o Safety Huddle.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await shJson('/api/safety-huddle/sectors', unidadeSelecionada);
      setSectors(data.items || []);
      setSeeAll(!!data.seeAll);
    } catch (e) {
      setError(e.message || 'Erro ao carregar setores.');
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await shJson('/api/safety-huddle/sectors', unidadeSelecionada, {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          integraContratos,
        }),
      });
      setOpenNew(false);
      setName('');
      setDescription('');
      setIntegraContratos(false);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="primary.main">
            Safety Huddle
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Hub de setores — dados isolados por unidade
            {unidadeSelecionada?.nome || unidadeSelecionada?.codigo
              ? ` (${unidadeSelecionada.nome || unidadeSelecionada.codigo})`
              : ''}
          </Typography>
        </Box>
        {canManage && seeAll && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={() => navigate('/safety-huddle/admin/setores')}>
              Gerenciar setores
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenNew(true)}>
              Novo setor
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {sectors.map((s) => (
            <Grid item xs={12} sm={6} md={4} key={s.id}>
              <Card
                elevation={0}
                sx={{
                  border: '1px solid rgba(25,118,210,0.15)',
                  borderRadius: 3,
                  height: '100%',
                  background: 'linear-gradient(135deg, #fff 0%, #f0f7ff 100%)',
                }}
              >
                <CardActionArea onClick={() => navigate(`/safety-huddle/setores/${s.id}`)} sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <HealthAndSafetyIcon color="primary" />
                      <Typography variant="h6" fontWeight={700}>
                        {s.name}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 40 }}>
                      {s.description || 'Sem descrição'}
                    </Typography>
                    <Typography variant="caption" color="primary.main" fontWeight={600}>
                      {s.formsCount} formulário(s)
                    </Typography>
                    {s.integraContratos && (
                      <Chip
                        size="small"
                        label="Integra contratos"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
          {!sectors.length && (
            <Grid item xs={12}>
              <Alert severity="info">Nenhum setor cadastrado nesta unidade.</Alert>
            </Grid>
          )}
        </Grid>
      )}

      <Dialog open={openNew} onClose={() => setOpenNew(false)} fullWidth maxWidth="sm">
        <DialogTitle>Novo setor</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Descrição"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={integraContratos}
                onChange={(e) => setIntegraContratos(e.target.checked)}
              />
            }
            label="Integrar contratos da unidade (prestadores)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNew(false)}>Cancelar</Button>
          <Button variant="contained" disabled={saving || !name.trim()} onClick={handleCreate}>
            Criar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
